import { queryOptions, useMutation } from '@tanstack/react-query'
import type { HTTPError } from 'ky'
import { handleResponse } from '../api/helpers.ts'
import { type CreateUserListDto, type UserListEntity, userManager } from '../api/user-manager.ts'
import { asQueryParam, useImpersonation } from './impersonation.ts'
import { impersonatedQueryData } from './queryHelpers.ts'

export const userListData = impersonatedQueryData((impersonation) =>
	queryOptions({
		queryKey: ['USER_LISTS', impersonation?.subscriptionId],
		queryFn: async () => {
			const userLists = await userManager
				.GET('/user-manager/contact-lists', { params: { query: asQueryParam(impersonation) } })
				.then(handleResponse)
			return userLists.toSorted((a, b) => a.name.localeCompare(b.name))
		},
		staleTime: 1000 * 60,
	}),
)

export const useCreateUserList = () => {
	const impersonation = useImpersonation()
	return useMutation<UserListEntity, HTTPError, CreateUserListDto>({
		mutationFn: (body) =>
			userManager.POST('/user-manager/contact-lists', { params: { query: asQueryParam(impersonation) }, body }).then(handleResponse),
		onSettled: () => userListData.invalidate(impersonation),
	})
}

export const useUpdateUserList = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: CreateUserListDto }) =>
			userManager.PATCH('/user-manager/contact-lists/{id}', {
				params: { path: { id }, query: asQueryParam(impersonation) },
				body,
			}),
		onSettled: () => userListData.invalidate(impersonation),
	})
}

export const useDeleteUserList = () => {
	const impersonation = useImpersonation()
	return useMutation({
		mutationFn: (id: string) =>
			userManager.DELETE('/user-manager/contact-lists/{id}', {
				params: { path: { id }, query: asQueryParam(impersonation) },
			}),
		onSettled: () => userListData.invalidate(impersonation),
	})
}
